import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';

interface ProfileDropdownProps {
  name: string;
  role?: string;
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getUserEmail(user: ReturnType<typeof useAuth>['user']) {
  try {
    const fromAuth = user?.profile?.email || (user?.profile as any)?.upn;
    if (fromAuth) return fromAuth as string;
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    return userInfo?.email || userInfo?.Email || userInfo?.upn || '';
  } catch {
    return '';
  }
}

export default function ProfileDropdown({ name, role = 'Cashier' }: ProfileDropdownProps) {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const initials = getInitials(name);
  const email = getUserEmail(user);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg px-3 py-2 transition-colors cursor-pointer"
      >
        <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
          {initials}
        </div>
        <div className="text-left">
          <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{name}</p>
          <p className="text-xs text-gray-500 whitespace-nowrap">{role}</p>
        </div>
        <i className={`ri-arrow-down-s-line text-lg text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white text-gray-900 rounded-xl shadow-xl border border-gray-100 py-4 px-4 z-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {initials}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{name}</p>
              {email && <p className="text-xs text-gray-500">{email}</p>}
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Role</span>
              <span className="text-sm font-semibold text-gray-900">{role}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Status</span>
              <span className="text-sm font-semibold text-green-600 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Online
              </span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => signOut()}
              className="flex items-center gap-2 text-red-600 hover:text-red-700 text-sm font-medium cursor-pointer"
            >
              <i className="ri-logout-box-r-line"></i>
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
